import React, { useMemo, useEffect } from 'react';
import { PageWithNavigation } from '@/src/components';
import {
  Card,
  Li,
  Margin,
  P,
  Strong,
  Ul,
  PrimaryCTAButton,
} from '@ovotech/element-v4';
import {
  BoltonProductType,
  PlanDetails,
  useChargeAnytimeProducts,
  EV,
  getPlanNameFromBolton,
} from '@monovo/ev-anytime';
import { useChargeAnytimeBoltonState } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';
import { PageHeaderV2 } from '../../components/PageHeaderV2';
import { Loading } from '@/src/pages/AnytimeEligibilityV2/components/Loading';
import { ROUTE_PLAN } from '@/src/constants/routes';
import { useHistory } from 'react-router-dom-v5';
import { useAnalytics } from '@ovotech/ui-tools';

const PublicChargingInfo = () => (
  <>
    <Margin bottom={2}>
      <Strong>Public Charging</Strong>
      <P>
        An annual public charging voucher to power your EV wherever you go. Plus
        plans include public charging for multiple EVs.
      </P>
    </Margin>
    <P>
      Our extensive network includes <Strong>50,000+ UK chargers</Strong> and{' '}
      <Strong>400,000+ chargers in Europe.</Strong>
    </P>
  </>
);

const PAYM = ({
  showPublicCharging = true,
}: {
  showPublicCharging?: boolean;
}) => (
  <>
    <Card style={{ padding: '16px' }}>
      <Margin bottom={4}>
        <P>
          <Strong>Pay monthly</Strong>
          <br />
          Mileage allowance is portioned out on a monthly basis.
        </P>
        <Ul>
          <Li>The miles reset at the start of each month</Li>
          <Li>The miles don’t roll over to the next month</Li>
        </Ul>
        <P>
          If you go over your monthly allowance you’ll be charged at your{' '}
          <Strong>standard tariff rate.</Strong>
        </P>
      </Margin>
      {showPublicCharging && <PublicChargingInfo />}
    </Card>
  </>
);

const PAYG = ({ planDetails }: { planDetails: PlanDetails }) => (
  <>
    <Margin top={4}>
      <Card style={{ padding: '16px' }}>
        <Margin bottom={2}>
          <P>
            <Strong>Pay as you go</Strong> gives you flat rate smart charging
            for {planDetails.pricePerKwh} per kWh.
          </P>
        </Margin>
        <P>Drive as much or as little as you want, no fee, no commitment.</P>
      </Card>
    </Margin>
  </>
);

export const SwitchConfirmationPage = () => {
  const history = useHistory();
  const { boltonPlan, isFetching } = useChargeAnytimeBoltonState();
  const { data: products } = useChargeAnytimeProducts('all');

  const { dispatch: analyticsDispatch } = useAnalytics();

  const allProducts = useMemo(() => (products ? products : []), [products]);

  const paygProduct = useMemo(() => {
    return allProducts.find(p => p.id === 'payg') || null;
  }, [allProducts]);

  const isPAYD = boltonPlan?.future?.productType === BoltonProductType.PAYD;
  const isOEM =
    boltonPlan?.future?.productType === BoltonProductType.PAYM_OEM_TIER_ONE;

  useEffect(() => {
    if (boltonPlan && !isFetching && products) {
      analyticsDispatch({
        type: 'view',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_SWITCH_SUCCESS_SCREEN_VIEWED,
        properties: {
          currentPlanName: getPlanNameFromBolton(boltonPlan.current, products),
          futurePlanName: getPlanNameFromBolton(boltonPlan.future, products),
          pathname: '/anytime/switch-confirmation',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.SUCCESSFUL_SWITCH,
        },
      });
    }
  }, [boltonPlan, isFetching, products]);

  if (!boltonPlan || isFetching || !paygProduct) {
    return <Loading />;
  }

  return (
    <PageWithNavigation title="EV Charge Anytime">
      <PageHeaderV2
        title="Great! You’ve switched plans."
        testId="switch-success-header"
      />
      <Margin top={8} />
      {isPAYD ? (
        <PAYG planDetails={paygProduct} />
      ) : (
        <PAYM showPublicCharging={!isOEM} />
      )}
      <Margin top={8} />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <PrimaryCTAButton onClick={() => history.push(ROUTE_PLAN)}>
          Back to my account
        </PrimaryCTAButton>
      </div>
    </PageWithNavigation>
  );
};
